import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { Camera, Upload } from 'lucide-react';

// One control for every upload in the app: "Take photo" opens the camera on a
// phone or iPad (rear lens), "Choose file" is the library / desktop picker.
// The forwarded ref is the real <input type="file"> the page reads at submit
// (`ref.current.files`) and clears (`ref.current.value = ''`), so existing
// submit code keeps working. A camera shot is copied into that same input.
type Props = {
  accept: string; // for "Choose file"; the camera input is always image/*
  multiple?: boolean;
  disabled?: boolean;
  /** Fires for either button; use this on sites that upload immediately. */
  onFiles?: (files: File[]) => void;
  chooseLabel?: string;
  photoLabel?: string;
  className?: string;
};

export const FilePicker = forwardRef<HTMLInputElement, Props>(function FilePicker(
  { accept, multiple, disabled, onFiles, chooseLabel = 'Choose file', photoLabel = 'Take photo', className = '' },
  ref
) {
  const library = useRef<HTMLInputElement>(null);
  const camera = useRef<HTMLInputElement>(null);
  const [, bump] = useState(0);
  useImperativeHandle(ref, () => library.current as HTMLInputElement, []);

  const names = Array.from(library.current?.files ?? []).map((f) => f.name);

  function fromCamera(list: FileList | null) {
    if (!list?.length || !library.current) return;
    const dt = new DataTransfer();
    if (multiple) Array.from(library.current.files ?? []).forEach((f) => dt.items.add(f));
    Array.from(list).forEach((f) => dt.items.add(f));
    library.current.files = dt.files;
    if (camera.current) camera.current.value = '';
    bump((n) => n + 1);
    onFiles?.(Array.from(dt.files));
  }

  return (
    <div className={className}>
      <input
        ref={library}
        type="file"
        accept={accept}
        multiple={multiple}
        className="sr-only"
        disabled={disabled}
        onChange={(e) => {
          bump((n) => n + 1);
          onFiles?.(Array.from(e.target.files ?? []));
        }}
      />
      <input
        ref={camera}
        type="file"
        accept="image/*"
        capture="environment"
        className="sr-only"
        disabled={disabled}
        onChange={(e) => fromCamera(e.target.files)}
      />
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          disabled={disabled}
          onClick={() => camera.current?.click()}
          className="btn-secondary inline-flex items-center gap-1.5 text-sm"
        >
          <Camera size={16} /> {photoLabel}
        </button>
        <button
          type="button"
          disabled={disabled}
          onClick={() => library.current?.click()}
          className="btn-secondary inline-flex items-center gap-1.5 text-sm"
        >
          <Upload size={16} /> {chooseLabel}
        </button>
      </div>
      {names.length > 0 && (
        <p className="mt-1.5 truncate text-xs text-ink-muted">
          {names.length === 1 ? names[0] : `${names.length} files: ${names.join(', ')}`}
        </p>
      )}
    </div>
  );
});
